// src/pages/tour-packages/TourPackageCreatePage.tsx

import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiX } from "react-icons/fi";

import AdminLayout from "@/layouts/AdminLayout";
import { useTourPackageCreatePage } from "./hooks/useTourPackageCreatePage";
import { CreateTourPackageForm } from "@/components/tour-packages/forms/CreateTourPackageForm";

/* ---------------------- Breadcrumbs ---------------------- */
function Breadcrumbs() {
  const { pathname } = useLocation();
  const parts = pathname.split("/").filter(Boolean);

  const crumbs = parts.map((p, idx) => {
    const href = "/" + parts.slice(0, idx + 1).join("/");
    const label = p
      .split("-")
      .map((s) => s[0]?.toUpperCase() + s.slice(1))
      .join(" ");
    return { href, label };
  });

  return (
    <nav className="text-xs text-gray-500">
      <ol className="flex flex-wrap items-center gap-1">
        <li>
          <Link to="/" className="hover:text-gray-700">
            Home
          </Link>
        </li>

        {crumbs.map((c, i) => (
          <li key={c.href} className="flex items-center gap-1">
            <span>/</span>
            {i < crumbs.length - 1 ? (
              <Link to={c.href} className="hover:text-gray-700">
                {c.label}
              </Link>
            ) : (
              <span className="text-gray-700">{c.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/* ============================================================================
   Page: Create Tour Package
============================================================================ */
export default function TourPackageCreatePage() {
  const navigate = useNavigate();

  const {
    form,
    handleSubmit,
    isSubmitting,
    citiesFieldArray,
    tourRatesFieldArray,
    itineraryDaysFieldArray,
    inclusionsFieldArray,
    exclusionsFieldArray,
    cancellationPoliciesFieldArray,
    hotelsFieldArray,
    imagesFieldArray,
    departuresFieldArray,
    tourTypes,
    suppliers,
    supplierLocked,
    isInitializing,
    initialLoadError,
    isSaving,
    saveError,
  } = useTourPackageCreatePage();

  const submitting = isSaving || isSubmitting;

  const handleCancel = () => {
    navigate("/tour/tour-packages");
  };

  const saveErrorMessage =
    (saveError as Error | null)?.message ??
    "Failed to save tour package. Please try again.";

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            Add Tour Package
          </h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>

        <button
          type="button"
          onClick={handleCancel}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          <FiX />
          Back to Tour Packages
        </button>
      </div>

      {/* Initial master-data loading / error states */}
      {isInitializing && (
        <div className="mx-auto max-w-6xl rounded-2xl border border-gray-200 bg-white p-4 text-sm text-slate-600 shadow-sm sm:p-6">
          Loading tour types and suppliers…
        </div>
      )}

      {initialLoadError && !isInitializing && (
        <div className="mx-auto max-w-6xl rounded-2xl border border-rose-100 bg-rose-50 p-4 text-sm text-rose-700 shadow-sm sm:p-6">
          Failed to load tour types or suppliers. Please refresh and try again.
        </div>
      )}

      {/* Main form */}
      {!isInitializing && !initialLoadError && (
        <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
          {saveError && (
            <div className="mb-4 rounded-md border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {saveErrorMessage}
            </div>
          )}

          <CreateTourPackageForm
            form={form}
            handleSubmit={handleSubmit}
            isSubmitting={submitting}
            citiesFieldArray={citiesFieldArray}
            tourRatesFieldArray={tourRatesFieldArray}
            itineraryDaysFieldArray={itineraryDaysFieldArray}
            inclusionsFieldArray={inclusionsFieldArray}
            exclusionsFieldArray={exclusionsFieldArray}
            cancellationPoliciesFieldArray={cancellationPoliciesFieldArray}
            hotelsFieldArray={hotelsFieldArray}
            imagesFieldArray={imagesFieldArray}
            departuresFieldArray={departuresFieldArray}
            tourTypes={tourTypes}
            suppliers={suppliers}
            supplierLocked={supplierLocked}
            onCancel={handleCancel}
          />
        </section>
      )}
    </AdminLayout>
  );
}
